import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Icon from "@/components/icon";
import ContentView from "@/features/content-v2/components/content-view";
import { PdfViewerScreen } from "@/features/content/pdf-viewer";
import { useIsDesktop } from "@/hooks/use-is-desktop";
import { downloadViaEitaa } from "@/utils/utilities";
import {
  Drawer,
  DrawerContent,
} from "@/components/ui/drawer";
import type { CourseContent } from "@/services/vitrin-service";

// File content (type 4) carries its URL on the attachment, the media token, or
// an external media url — mirror the resolution used by ContentView.
const resolveFileUrl = (content: CourseContent) =>
  content.attachments?.[0]?.url ||
  content.external_media?.media_token ||
  content.external_media?.media_url ||
  "";

const isPdfFile = (content: CourseContent, url: string) => {
  const attachment = content.attachments?.[0];
  return (
    attachment?.mime_type === "application/pdf" ||
    /\.pdf(\?|#|$)/i.test(attachment?.file_name || "") ||
    /\.pdf(\?|#|$)/i.test(url) ||
    /pdf/i.test(content.external_media?.media_type || "")
  );
};

const getFileExtension = (fileName?: string, url?: string) => {
  const source = fileName || url || "";
  const match = /\.([a-z0-9]{1,5})(?:[?#]|$)/i.exec(source);
  return match ? match[1].toLowerCase() : "";
};

const formatFileSize = (bytes?: number) => {
  if (!bytes || bytes <= 0) return "";
  const units = ["بایت", "کیلوبایت", "مگابایت", "گیگابایت"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded =
    value >= 10 || unit === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded.toLocaleString("fa-IR")} ${units[unit]}`;
};

// One fixed, solid squircle color per content type — "Your Info" card style.
// (0:text, 1:image, 2:video, 3:audio, 4:file)
const TYPE_COLOR: Record<number, string> = {
  0: "#3aa0f0", // text  - blue
  1: "#34c759", // image - green
  2: "#e0556e", // video - red
  3: "#f5a623", // audio - orange
  4: "#5b8def", // file  - indigo
};
const FALLBACK_COLOR = "#5b8def";

// Sprite icon per content type (0:text, 1:image, 2:video, 3:audio, 4:file).
const TYPE_ICON: Record<number, string> = {
  0: "text-fill",
  1: "image-fill",
  2: "video-square-fill",
  3: "audio-fill",
  4: "document-fill",
};

/**
 * The one visual per content type — a solid coloured squircle with a white line
 * glyph ("Your Info" card style). Shared by the playlist rows and the header of
 * the desktop stage so a row and the thing it opened read as the same object.
 */
function TypeBadge({ type }: { type: number }) {
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-xl p-2 text-white"
      style={{ backgroundColor: TYPE_COLOR[type] ?? FALLBACK_COLOR }}
    >
      <Icon name={TYPE_ICON[type] ?? "document-fill"} size={20} />
    </span>
  );
}

type AnalyticsContext = {
  miniappId?: string;
  courseId?: number;
  lessonId?: number;
};

type Props = {
  contents: CourseContent[];
  lessonTitle?: string;
  analyticsContext?: AnalyticsContext;
  hasNextPage?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
};

const fa = (n: number) => n.toLocaleString("fa-IR");

// Per content type (0:text, 1:image, 2:video, 3:audio, 4:file).
const TYPE_LABELS: Record<number, string> = {
  0: "متن",
  1: "تصویر",
  2: "ویدیو",
  3: "صوت",
  4: "فایل",
};

// Video & audio play inline in the top viewer; everything else opens in a popup.
const isMediaType = (type: number) => type === 2 || type === 3;

const stripHtml = (html: string) =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&[a-z]+;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

type PlaylistItem = {
  content: CourseContent;
  title: string;
  typeLabel: string;
  isMedia: boolean;
  /** type === 4: a downloadable file (PDF or otherwise). */
  isFile: boolean;
  fileUrl: string;
  isPdf: boolean;
  ext: string;
  sizeLabel: string;
};

export default function LessonPlaylist({
  contents,
  lessonTitle,
  analyticsContext,
  hasNextPage,
  isFetchingMore,
  onLoadMore,
}: Props) {
  const items = useMemo<PlaylistItem[]>(() => {
    const perType: Record<number, number> = {};
    return contents.map((content) => {
      const label = TYPE_LABELS[content.type] ?? content.type_label ?? "محتوا";
      perType[content.type] = (perType[content.type] ?? 0) + 1;
      const order = perType[content.type];
      const fileName = content.attachments?.[0]?.file_name?.trim();

      let title: string;
      if (content.type === 0) {
        const text = stripHtml(content.text_content || "");
        title = text
          ? text.length > 70
            ? `${text.slice(0, 70)}…`
            : text
          : `${label} ${fa(order)}`;
      } else {
        title = fileName || `${label} ${fa(order)}`;
      }

      const isFile = content.type === 4;
      const fileUrl = isFile ? resolveFileUrl(content) : "";
      const ext = isFile ? getFileExtension(fileName, fileUrl) : "";

      return {
        content,
        title,
        typeLabel: label,
        isMedia: isMediaType(content.type),
        isFile,
        fileUrl,
        isPdf: isFile && isPdfFile(content, fileUrl),
        ext,
        sizeLabel: formatFileSize(content.attachments?.[0]?.size),
      };
    });
  }, [contents]);

  const hasMedia = useMemo(() => items.some((item) => item.isMedia), [items]);

  // Desktop turns the stage into a general viewer: the wide column has room for
  // an article or an image, so everything but a file opens there instead of in
  // a popup. On a phone the stage stays media-only — a text row that replaced
  // the player would push the playlist off screen.
  const isDesktop = useIsDesktop();
  const stageItems = useMemo(
    () => items.filter((item) => (isDesktop ? !item.isFile : item.isMedia)),
    [items, isDesktop],
  );

  const [activeId, setActiveId] = useState<number | null>(null);
  // Popup state is split from the item so its content survives the close animation.
  const [popupItem, setPopupItem] = useState<PlaylistItem | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  // Open PDF files in the full-screen reader, straight from the playlist row.
  const [pdfViewer, setPdfViewer] = useState<{ url: string; title: string } | null>(null);

  const viewerRef = useRef<HTMLDivElement>(null);
  const hasUserSelectedRef = useRef(false);

  // Keep the stage pointed at a valid item as contents paginate in — and as the
  // viewport crosses the desktop breakpoint, which changes what may be staged.
  useEffect(() => {
    if (!stageItems.length) {
      setActiveId(null);
      return;
    }
    setActiveId((prev) =>
      prev != null && stageItems.some((item) => item.content.id === prev)
        ? prev
        : (stageItems.find((item) => item.isMedia) ?? stageItems[0]).content.id,
    );
  }, [stageItems]);

  const active = useMemo(
    () => items.find((item) => item.content.id === activeId) ?? null,
    [items, activeId],
  );

  // Growing past the breakpoint with the drawer open: the desktop stage is
  // where that content belongs now, so hand it over rather than leaving a
  // modal covering the layout it was never part of.
  useEffect(() => {
    if (!isDesktop || !popupOpen) return;
    setPopupOpen(false);
    if (popupItem) setActiveId(popupItem.content.id);
  }, [isDesktop, popupOpen, popupItem]);

  // After picking a track, anchor the player to the top *before* paint so the
  // height difference between media types never shoves the page (no jump).
  // Desktop keeps the stage in place at the top of the column, so there is
  // nothing to anchor — scrolling there would only yank the page.
  useLayoutEffect(() => {
    if (!hasUserSelectedRef.current || isDesktop) return;
    viewerRef.current?.scrollIntoView({ block: "start" });
  }, [activeId, isDesktop]);

  if (!items.length) return null;

  // Anything stageable goes to the viewer; on a phone that leaves text/image to
  // the drawer. Files are handled by their own inline action buttons
  // (download / view), not here.
  const handleSelect = (item: PlaylistItem) => {
    if (item.isMedia || isDesktop) {
      hasUserSelectedRef.current = true;
      setPopupOpen(false);
      setActiveId(item.content.id);
    } else {
      setPopupItem(item);
      setPopupOpen(true);
    }
  };

  return (
    // Phone: one viewport-tall column — stage pinned on top, playlist scrolling
    // under it. Desktop: stage and playlist side by side, the page itself the
    // only scroller, so a long article can run past the fold while the playlist
    // sticks. The stage comes first in the DOM, which in RTL puts it on the
    // right and the playlist rail on the left.
    <div
      dir="rtl"
      className="content-font flex h-[calc(100svh_-_var(--tg-safe-area-inset-top,0px)_-_48px)] flex-col lg:grid lg:h-auto lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start lg:gap-6"
    >
      {active ? (
        <div
          ref={viewerRef}
          className={`min-w-0 shrink-0 bg-background ${
            active.isMedia
              ? // Rounding the frame rather than the player keeps it square in
                // fullscreen, where it is no longer this box's child in spirit.
                "lg:overflow-hidden lg:rounded-2xl"
              : ""
          } ${
            // A player with no picture has no use for the full width of the
            // stage; the article and the video do.
            active.content.type === 3 ? "lg:max-w-2xl" : ""
          }`}
        >
          {active.isMedia ? (
            <ContentView
              key={active.content.id}
              content={active.content}
              lessonTitle={lessonTitle}
              analyticsContext={analyticsContext}
            />
          ) : (
            // Desktop only — the phone sends these to the drawer. Framed as a
            // sheet so a bare paragraph doesn't float in the empty column.
            <article className="rounded-2xl border border-default-100 bg-background">
              <header className="flex items-center gap-3 border-b border-default-100 px-5 py-4">
                <TypeBadge type={active.content.type} />
                <div className="min-w-0">
                  <h2 className="truncate text-sm font-bold text-default-900">
                    {active.title}
                  </h2>
                  <p className="mt-0.5 text-xs text-default-500">
                    {active.typeLabel}
                  </p>
                </div>
              </header>
              <div className="px-5 py-4">
                <ContentView
                  key={active.content.id}
                  content={active.content}
                  lessonTitle={lessonTitle}
                  analyticsContext={analyticsContext}
                />
              </div>
            </article>
          )}
        </div>
      ) : isDesktop ? (
        <div className="flex min-h-[320px] min-w-0 items-center justify-center rounded-2xl border border-dashed border-default-200 px-6 text-center text-sm text-default-400">
          برای مشاهده، یکی از محتواهای این جلسه را انتخاب کنید.
        </div>
      ) : null}

      {/* Phone: the only scrolling region; the padding clears the fixed bottom
          nav. Desktop: a rail that sticks beside the stage and scrolls itself
          once the list outgrows the viewport. */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-32 lg:sticky lg:top-4 lg:max-h-[calc(100svh_-_2rem)] lg:flex-none lg:rounded-2xl lg:border lg:border-default-100 lg:pb-2">
        <h3 className="px-4 pb-1 pt-3 text-xs font-semibold text-primary lg:sticky lg:top-0 lg:z-10 lg:bg-background/90 lg:px-5 lg:py-3 lg:backdrop-blur">
          {hasMedia ? "لیست محتوا" : "محتواها"}
          <span className="mr-1 font-normal text-default-400">
            ({fa(items.length)})
          </span>
        </h3>

        <ul className="px-2 pb-1 lg:px-3">
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            const isStaged = item.content.id === activeId;
            const isOpenPopup =
              !item.isMedia &&
              !item.isFile &&
              popupOpen &&
              item.content.id === popupItem?.content.id;
            const highlighted = isStaged || isOpenPopup;

            const iconNode = <TypeBadge type={item.content.type} />;

            // Title + label + trailing affordance. The hairline divider lives on
            // this block so it starts after the icon (inset, settings-list style)
            // and is dropped on the last row.
            const detail = (
              <div
                className={`flex min-w-0 flex-1 items-center gap-2 py-3 ${
                  isLast ? "" : "border-b border-default-200"
                }`}
              >

                <div className="min-w-0 flex-1 text-right">
                  <p
                    className={`line-clamp-1 text-sm leading-snug ${
                      highlighted
                        ? "font-bold text-primary"
                        : "font-semibold text-default-900"
                    }`}
                  >
                    {item.title}
                  </p>
                  <p
                    className={`mt-0.5 text-xs ${
                      highlighted ? "text-primary/70" : "text-default-500"
                    }`}
                  >
                    {isStaged
                      ? item.isMedia
                        ? "در حال پخش"
                        : "در حال نمایش"
                      : item.isFile
                        ? [item.ext.toUpperCase(), item.sizeLabel]
                            .filter(Boolean)
                            .join(" · ") || item.typeLabel
                        : item.typeLabel}
                  </p>
                </div>

                {/* Trailing affordance per row kind. */}
                {item.isFile ? (
                  <div className="flex shrink-0 items-center gap-2">
                    {item.isPdf && item.fileUrl ? (
                      <button
                        type="button"
                        onClick={() =>
                          setPdfViewer({ url: item.fileUrl, title: item.title })
                        }
                        className="rounded-full bg-primary/10 px-3.5 py-1.5 text-xs font-semibold text-primary transition-colors active:bg-primary/20 lg:hover:bg-primary/20"
                      >
                        مشاهده
                      </button>
                    ) : null}

                    <button
                      type="button"
                      onClick={() =>
                        item.fileUrl && downloadViaEitaa(item.fileUrl, item.title)
                      }
                      disabled={!item.fileUrl}
                      aria-label="دانلود"
                      className="flex size-9 items-center justify-center rounded-full bg-content3 text-default-600 transition-colors active:bg-content4 disabled:opacity-50 lg:hover:bg-content4"
                    >
                      <Icon name="download" size={18} />
                    </button>
                  </div>
                ) : !item.isMedia ? (
                  <Icon
                    name="chevron-left"
                    className="size-4 shrink-0 text-default-300"
                  />
                ) : null}
              </div>
            );

            // Files carry their own action buttons, so their row is a plain
            // container; media/text/image rows are tappable as a whole.
            return (
              <li key={item.content.id}>
                {item.isFile ? (
                  <div
                    className={`flex items-center gap-3 rounded-xl px-2 ${
                      highlighted ? "bg-primary/5" : ""
                    }`}
                  >
                    {iconNode}
                    {detail}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleSelect(item)}
                    aria-current={highlighted ? "true" : undefined}
                    className={`flex w-full items-center gap-3 rounded-xl px-2 text-right transition-colors ${
                      highlighted
                        ? "bg-primary/5"
                        : "active:bg-content2 lg:hover:bg-content2"
                    }`}
                  >
                    {iconNode}
                    {detail}
                  </button>
                )}
              </li>
            );
          })}
        </ul>

        {hasNextPage ? (
          <div className="px-4 pt-2 lg:px-3 lg:pb-1">
            <button
              type="button"
              onClick={onLoadMore}
              disabled={isFetchingMore}
              className="w-full rounded-lg bg-content3 p-3 text-sm font-medium text-default-700 transition-colors disabled:opacity-70 lg:hover:bg-content4"
            >
              {isFetchingMore ? "در حال بارگذاری..." : "نمایش محتوای بیشتر"}
            </button>
          </div>
        ) : null}
      </div>

      {/* Popup for text / image content */}
      <Drawer open={popupOpen} onOpenChange={setPopupOpen}>
        <DrawerContent>
          {popupItem ? (
            <>

              <div className="max-h-[72vh] overflow-y-auto overscroll-contain content-font">
                <ContentView
                  content={popupItem.content}
                  lessonTitle={lessonTitle}
                  analyticsContext={analyticsContext}
                />
              </div>
            </>
          ) : null}
        </DrawerContent>
      </Drawer>

      {/* Full-screen PDF reader, opened directly from a file row. */}
      {pdfViewer ? (
        <PdfViewerScreen
          url={pdfViewer.url}
          title={pdfViewer.title}
          onClose={() => setPdfViewer(null)}
        />
      ) : null}
    </div>
  );
}
